import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Zap, Edit, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function HowItWorks() {
  const {
    t
  } = useLanguage();
  const steps = [{
    icon: MessageSquare,
    title: t.howItWorks.step1,
    desc: t.howItWorks.step1Desc,
    color: 'from-blue-500 to-indigo-600',
    shadow: 'shadow-blue-500/25'
  }, {
    icon: Zap,
    title: t.howItWorks.step2,
    desc: t.howItWorks.step2Desc,
    color: 'from-purple-500 to-pink-600',
    shadow: 'shadow-purple-500/25'
  }, {
    icon: Edit,
    title: t.howItWorks.step3,
    desc: t.howItWorks.step3Desc,
    color: 'from-orange-500 to-red-600',
    shadow: 'shadow-orange-500/25'
  }, {
    icon: CheckCircle,
    title: t.howItWorks.step4,
    desc: t.howItWorks.step4Desc,
    color: 'from-green-500 to-emerald-600',
    shadow: 'shadow-green-500/25'
  }];
  return <section className="py-32 bg-white dark:bg-[#050505] transition-colors duration-500 relative overflow-hidden" data-id="element-215">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-216">
        <div className="text-center mb-24" data-id="element-217">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-218">
            {t.howItWorks.title}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto" data-id="element-219">
            {t.howItWorks.subtitle}
          </motion.p>
        </div>

        <div className="relative" data-id="element-220">
          {/* Animated Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-16 left-0 w-full h-1 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden" data-id="element-221">
            <motion.div initial={{
            width: '0%'
          }} whileInView={{
            width: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500" data-id="element-222" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10" data-id="element-223">
            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.3
          }} className="group relative" data-id="element-224">
                {/* Step Card */}
                <div className="flex flex-col items-center text-center" data-id="element-225">
                  {/* Premium Icon Container */}
                  <div className="relative mb-8" data-id="element-226">
                    {/* Pulsing Glow */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-full blur-xl opacity-20 group-hover:opacity-40 animate-pulse-slow transition-opacity duration-500`} data-id="element-227" />

                    {/* Main Icon Circle */}
                    <div className={`relative w-32 h-32 rounded-full bg-white dark:bg-[#0A0A0A] border-4 border-gray-50 dark:border-[#151515] flex items-center justify-center shadow-2xl ${step.shadow} group-hover:scale-105 transition-transform duration-500`} data-id="element-228">
                      {/* Inner Gradient Border */}
                      <div className={`absolute inset-2 rounded-full border-2 border-transparent bg-gradient-to-br ${step.color} opacity-20`} style={{
                    maskImage: 'linear-gradient(white, white)',
                    WebkitMaskImage: 'linear-gradient(white, white)',
                    maskComposite: 'exclude',
                    WebkitMaskComposite: 'xor'
                  }} data-id="element-229" />

                      <step.icon className={`w-12 h-12 text-transparent bg-clip-text bg-gradient-to-br ${step.color}`} data-id="element-230" />

                      {/* Step Number Badge */}
                      <div className={`absolute -top-2 -right-2 w-10 h-10 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-lg shadow-lg border-4 border-white dark:border-[#0A0A0A]`} data-id="element-231">
                        {index + 1}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative px-4 py-6 rounded-2xl bg-transparent group-hover:bg-gray-50 dark:group-hover:bg-white/5 transition-colors duration-300" data-id="element-232">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 dark:group-hover:from-white dark:group-hover:to-gray-300 transition-all" data-id="element-233">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed" data-id="element-234">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* Arrow for mobile */}
                {index < steps.length - 1 && <div className="md:hidden flex justify-center my-4 text-gray-300 dark:text-gray-700" data-id="element-235">
                    <ArrowRight className="w-6 h-6 rotate-90" data-id="element-236" />
                  </div>}
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}